import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! Shows a configured custom field the way it will actually look once it is filled in,\n    instead of naming its data_type. Args:\n      @organizationCustomField — the OCF record being configured (required)\n\n    Inert on purpose: aria-hidden plus pointer-events:none in .bb-cf-preview. This is a\n    picture of a field, not a field — it must not take focus, must not swallow the row\'s\n    drag handle, and must not let anyone type into a value that has nowhere to be saved.\n    Keeping it inert is also what stops CfAdvancedTextarea from ever mounting Quill here.\n    No @applicationController either, so the extra tools have nothing to wire into.\n\n    Drawn only once nearly on screen (see the backing class) — these lists run to hundreds\n    of rows. Until then the box just holds its height so the scrollbar stays honest. }}\n<div\n  class=\'bb-cf-preview\'\n  aria-hidden=\'true\'\n  inert\n  {{did-insert this.watch}}\n  {{will-destroy this.unwatch}}\n>\n  {{#if this.onScreen}}\n    <EditCustomField @custom_field={{this.customField}} @hideExtraTools={{true}} />\n  {{/if}}\n</div>\n");

var _class, _descriptor;
const PRERENDER_MARGIN = '400px';

// Sample values per data_type, so a preview shows a field that looks lived-in rather
// than an empty widget. Kept deliberately generic — these are illustrations of the
// field's shape, not of any tenant's real data.
const SAMPLES = {
  text: {
    value: 'Mario Rossi'
  },
  textarea: {
    value: 'Testo libero su piu righe.'
  },
  advancedTextarea: {
    value: '<p>Testo <b>formattato</b> di esempio.</p>'
  },
  number: {
    number_value: 42
  },
  currency: {
    number_value: 100
  },
  checkbox: {
    boolean_value: true
  },
  suggestion: {
    value: 'Valore suggerito'
  },
  calculated: {
    number_value: 128
  }
};
let BbCustomFieldPreviewComponent = (_class = class BbCustomFieldPreviewComponent extends Component {
  constructor(...args) {
    super(...args);
    // Args:
    //   @organizationCustomField — the real OCF record being configured
    // A rendered field costs far more than the line of text it replaces, and these lists get
    // long: settings/custom-field filtered by project shows 867 fields on a real clinic
    // tenant, which is ~20s of blocked main thread if every one of them is drawn at once.
    // So draw a row only once it is nearly on screen. Once drawn it stays drawn — scrolling
    // back and forth must not re-render.
    _initializerDefineProperty(this, "onScreen", _descriptor, this);
    this._observer = null;
  }
  watch(element) {
    // Everything is on screen at once for anyone without IntersectionObserver; better a
    // slow page than a list of empty rows.
    if (typeof IntersectionObserver === 'undefined') {
      this.onScreen = true;
      return;
    }
    this._observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.onScreen = true;
        this.unwatch();
      }
    }, {
      rootMargin: PRERENDER_MARGIN
    });
    this._observer.observe(element);
  }
  unwatch() {
    this._observer?.disconnect();
    this._observer = null;
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this.unwatch();
  }

  // EditCustomField expects a custom_field record: { value, number_value, boolean_value,
  // organization_custom_field }. A settings page has no such record — the field is a
  // definition, not a filled-in value — so hand it a plain object instead. Plain, not an
  // Ember Data record, so nothing lands in the store and nothing can be saved by accident.
  //
  // A getter, not constructor state: it reads the OCF's tracked attributes, so renaming a
  // field or changing its type in the edit modal re-renders the preview.
  get customField() {
    const ocf = this.args.organizationCustomField;
    const dataType = ocf?.data_type;
    return {
      value: null,
      number_value: null,
      boolean_value: null,
      ...this.sample,
      organization_custom_field: {
        name: ocf?.name,
        data_type: dataType,
        // Mirror, never the record itself: EditCustomField renders nothing for a disabled
        // field, and a disabled field still has to show up in the settings list (dimmed by
        // the row, see .custom_fields_list). The preview is about shape, not state.
        disabled: false,
        custom_field_definitions: this.definitions
      }
    };
  }

  // A dropbox's sample can only come from its own options, so it is resolved here rather
  // than from the static table: an unselected select reads as an empty field, which is
  // the opposite of what these previews are for.
  get sample() {
    const dataType = this.args.organizationCustomField?.data_type;
    if (dataType === 'dropbox') {
      const first = this.definitions[0]?.name;
      return first ? {
        value: first
      } : {};
    }
    return SAMPLES[dataType] ?? {};
  }

  // dropbox reads the definition names as its options; range and table stash their JSON
  // config in the first definition's name. Both want the real values — a preview of a
  // 1-to-5 scale has to show 1 to 5.
  get definitions() {
    const definitions = this.args.organizationCustomField?.custom_field_definitions ?? [];
    return definitions.map(definition => ({
      name: definition.name
    }));
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "onScreen", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "watch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "watch"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unwatch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unwatch"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbCustomFieldPreviewComponent);

export { BbCustomFieldPreviewComponent as default };
//# sourceMappingURL=bb-custom-field-preview.js.map
